<template>
  <teleport to="body">
    <div
      class="z-[99999] fixed inset-0 flex items-center justify-center bg-[#20202050] px-5"
      style="backdrop-filter: blur(4px)"
      @click.self="$emit('close')"
    >
      <div class="pop relative w-full max-w-[440px] sm:max-w-[480px] rounded-[16px] overflow-hidden bg-black shadow-lg">
        <div class="shadow"></div>
        <img
          src="@/assets/images/wasabi/RemoveItemBg.png"
          alt=""
          class="absolute inset-0 z-0 h-full w-full scale-[2] rounded-[16px] object-cover"
        />

        <div class="relative z-10 flex max-h-[82vh] flex-col gap-5 p-6 text-white">
          <div class="flex justify-end">
            <button
              type="button"
              class="close-button flex h-10 w-10 items-center justify-center rounded-[999px] border-2 border-[#75B13B70] bg-black/30"
              @click="$emit('close')"
              :aria-label="$t('wasabi.orderEditPopup.close')"
            >
              <span class="block text-2xl leading-none text-[#DBE1F0]">&times;</span>
            </button>
          </div>

          <div class="flex flex-col gap-2 text-center">
            <p class="text-[26px] font-bold leading-[30px]">
              {{ $t('wasabi.orderEditPopup.title') }}
            </p>
            <p class="text-sm font-medium leading-[150%] text-[#DBE1F0]">
              {{ orderLabel }}
            </p>
            <p class="text-xs font-semibold leading-[150%] text-[#DBE1F0]">
              {{ $t('wasabi.orderEditPopup.finalHint') }}
            </p>
          </div>

          <div class="custom-scroll-wrap">
            <div
              ref="scrollContent"
              class="custom-scroll-content"
              @scroll="updateCustomScrollbar"
            >
              <div v-if="!hasChanges" class="rounded-[14px] border border-[#75B13B57] bg-[#344425]/80 p-4 text-sm">
                {{ $t('wasabi.orderEditPopup.fallback') }}
              </div>

              <div v-if="infoChanges.length" class="change-block">
              <p class="change-title">{{ $t('wasabi.orderEditPopup.orderInfo') }}</p>
              <div v-for="change in infoChanges" :key="`info-${change.key}`" class="change-detail-row">
                <span>{{ change.label }}</span>
                <span class="change-value">
                  <span>{{ change.oldValue }}</span>
                  <span class="change-arrow" aria-hidden="true">➜</span>
                  <span>{{ change.newValue }}</span>
                </span>
              </div>
            </div>

              <div v-if="added.length" class="change-block">
                <p class="change-title">{{ $t('wasabi.orderEditPopup.added') }}</p>
                <div v-for="dish in added" :key="dish.key" class="dish-change">
                  <div class="change-row py-0">
                    <span>{{ dish.name }}</span>
                  <span v-if="dish.quantity">+{{ dish.quantity }}</span>
                </div>
                <p v-for="detail in dish.details" :key="detail.key" class="change-detail">
                  <span>{{ detail.label }}: </span>
                  <span v-if="detail.singleValue">{{ detail.singleValue }}</span>
                  <span v-else class="change-value">
                    <span>{{ detail.oldValue }}</span>
                    <span class="change-arrow" aria-hidden="true">➜</span>
                    <span>{{ detail.newValue }}</span>
                  </span>
                </p>
              </div>
            </div>

              <div v-if="removed.length" class="change-block">
                <p class="change-title">{{ $t('wasabi.orderEditPopup.removed') }}</p>
                <div v-for="dish in removed" :key="dish.key" class="dish-change">
                  <div class="change-row py-0">
                    <span>{{ dish.name }}</span>
                  <span v-if="dish.quantity">-{{ dish.quantity }}</span>
                </div>
                <p v-for="detail in dish.details" :key="detail.key" class="change-detail">
                  <span>{{ detail.label }}: </span>
                  <span v-if="detail.singleValue">{{ detail.singleValue }}</span>
                  <span v-else class="change-value">
                    <span>{{ detail.oldValue }}</span>
                    <span class="change-arrow" aria-hidden="true">➜</span>
                    <span>{{ detail.newValue }}</span>
                  </span>
                </p>
              </div>
            </div>

              <div v-if="changed.length" class="change-block">
                <p class="change-title">{{ $t('wasabi.orderEditPopup.changed') }}</p>
                <div v-for="dish in changed" :key="dish.key" class="dish-change">
                <div class="change-row py-0">
                  <span>{{ dish.name }}</span>
                </div>
                <p v-for="detail in dish.details" :key="detail.key" class="change-detail">
                  <span>{{ detail.label }}: </span>
                  <span v-if="detail.singleValue">{{ detail.singleValue }}</span>
                  <span v-else class="change-value">
                    <span>{{ detail.oldValue }}</span>
                    <span class="change-arrow" aria-hidden="true">➜</span>
                    <span>{{ detail.newValue }}</span>
                  </span>
                </p>
              </div>
            </div>
            </div>

            <div v-if="customScrollbar.visible" class="custom-scrollbar" aria-hidden="true">
              <div
                class="custom-scrollbar-thumb"
                :style="{
                  height: `${customScrollbar.thumbHeight}px`,
                  transform: `translateY(${customScrollbar.thumbTop}px)`,
                }"
              />
            </div>
          </div>

          <button
            type="button"
            class="min-h-[54px] rounded-[14px] border border-[#75B13B] px-3 text-lg font-extrabold"
            @click="viewFinalOrder"
          >
            {{ $t('wasabi.orderEditPopup.viewFinal') }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
const props = defineProps<{
  orderEditLog: any
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const dishesStore = useDishesStore()
const { symbol } = storeToRefs(dishesStore)
const { t: $t } = useI18n()
const router = useRouter()
const route = useRoute()
const scrollContent = ref<HTMLElement | null>(null)
const customScrollbar = reactive({
  visible: false,
  thumbHeight: 0,
  thumbTop: 0,
})

let resizeObserver: ResizeObserver | null = null
let previousBodyOverflow = ''
let previousBodyPosition = ''
let previousBodyTop = ''
let previousBodyWidth = ''
let lockedScrollY = 0

const order = computed(() => props.orderEditLog?.order)
const eventChanges = computed(() => props.orderEditLog?.changes ?? null)
const resourceChanges = computed(() => order.value?.dish_changes ?? {})

const fieldLabels: Record<string, string> = {
  quantity: 'quantity',
  price: 'price',
  total: 'total',
  comment: 'comment',
  our_comment: 'ourComment',
  add_ons: 'addOns',
  option_combain_id: 'option',
  type: 'type',
  table_id: 'table',
  delivery_full_name: 'deliveryFullName',
  delivery_email: 'deliveryEmail',
  delivery_phone: 'deliveryPhone',
  delivery_address: 'deliveryAddress',
}

const formatAmount = (value: number | string | null | undefined) => {
  const amount = Number(value || 0)
  return `${symbol.value}${amount.toFixed(2)}`
}

const formatValue = (key: string, value: unknown) => {
  if (value === null || value === undefined || value === '') return $t('wasabi.orderEditPopup.emptyValue')
  if (key === 'price' || key === 'total') return formatAmount(value as number | string)
  if (key === 'type') return String(value).replace(/_/g, ' ')
  if (Array.isArray(value)) return value.map((item) => formatValue(key, item)).join(', ')
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).map((item) => formatValue(key, item)).join(', ')
  return String(value)
}

const getFieldLabel = (key: string) => {
  const labelKey = fieldLabels[key] ?? key
  return $t(`wasabi.orderEditPopup.fields.${labelKey}`)
}

const makeDetail = (key: string, value: any) => ({
  key,
  label: getFieldLabel(key),
  oldValue: formatValue(key, value?.old),
  newValue: formatValue(key, value?.new),
  singleValue: null as string | null,
})

const makeAddedPriceDetail = (value: unknown) => ({
  key: 'price',
  label: getFieldLabel('price'),
  oldValue: '',
  newValue: '',
  singleValue: `+ ${formatAmount(value as number | string)}`,
})

const makeRemovedPriceDetail = (value: unknown) => ({
  key: 'price',
  label: getFieldLabel('price'),
  oldValue: '',
  newValue: '',
  singleValue: `- ${formatAmount(value as number | string)}`,
})

const getRemovedDishPrice = (dish: any) => {
  const removedDishes = resourceChanges.value?.removed
  if (!Array.isArray(removedDishes)) return null

  const removedDish = removedDishes.find((item: any) => {
    return (
      (dish.item_id && item.dish_id && dish.item_id == item.dish_id) ||
      (dish.name && item.name && dish.name === item.name)
    )
  })

  return removedDish?.price ?? null
}

const eventDishChanges = computed(() => {
  const dishes = eventChanges.value?.dishes
  if (!Array.isArray(dishes)) return null

  return dishes.map((dish: any, index: number) => {
    const quantityChange = dish.changes?.quantity
    const oldQuantity = Number(quantityChange?.old ?? Number.NaN)
    const newQuantity = Number(quantityChange?.new ?? Number.NaN)
    const type = oldQuantity === 0 ? 'added' : newQuantity === 0 ? 'removed' : 'changed'
    const details = Object.entries(dish.changes ?? {})
      .filter(([key]) => !(key === 'quantity' && (type === 'added' || type === 'removed')))
      .map(([key, value]: [string, any]) => {
        if (type === 'added' && key === 'price') return makeAddedPriceDetail(value?.new)
        if (type === 'removed' && key === 'price') return makeRemovedPriceDetail(value?.old)
        return makeDetail(key, value)
      })

    if (type === 'removed' && !details.some((detail) => detail.key === 'price')) {
      const removedPrice = getRemovedDishPrice(dish)
      if (removedPrice) {
        details.push(makeRemovedPriceDetail(removedPrice))
      }
    }

    return {
      key: `${dish.item_id ?? index}-${dish.name}-${type}`,
      name: dish.name,
      quantity: Number.isFinite(newQuantity - oldQuantity) ? Math.abs(newQuantity - oldQuantity) : null,
      details,
      type,
    }
  })
})

const oldResourceDishChanges = computed(() => ({
  added: (resourceChanges.value?.added ?? []).map((dish: any, index: number) => ({
    key: `added-${dish.id ?? index}-${dish.name}`,
    name: dish.name,
    quantity: dish.quantity,
    details: dish.price ? [makeAddedPriceDetail(dish.price)] : [],
    type: 'added',
  })),
  removed: (resourceChanges.value?.removed ?? []).map((dish: any, index: number) => ({
    key: `removed-${dish.id ?? index}-${dish.name}`,
    name: dish.name,
    quantity: dish.quantity,
    details: dish.price ? [makeRemovedPriceDetail(dish.price)] : [],
    type: 'removed',
  })),
  changed: (resourceChanges.value?.changed ?? []).map((dish: any, index: number) => {
    const details = [
      dish.quantity_before !== dish.quantity_after ? makeDetail('quantity', { old: dish.quantity_before, new: dish.quantity_after }) : null,
      dish.price_before !== dish.price_after ? makeDetail('price', { old: dish.price_before, new: dish.price_after }) : null,
      dish.note_before !== dish.note_after ? makeDetail('comment', { old: dish.note_before, new: dish.note_after }) : null,
      dish.our_note_before !== dish.our_note_after ? makeDetail('our_comment', { old: dish.our_note_before, new: dish.our_note_after }) : null,
    ].filter((detail): detail is ReturnType<typeof makeDetail> => Boolean(detail))

    return {
      key: `changed-${dish.id ?? index}-${dish.name}`,
      name: dish.name,
      quantity: null,
      details,
      type: 'changed',
    }
  }),
}))

const added = computed(() => eventDishChanges.value?.filter((dish) => dish.type === 'added') ?? oldResourceDishChanges.value.added)
const removed = computed(() => eventDishChanges.value?.filter((dish) => dish.type === 'removed') ?? oldResourceDishChanges.value.removed)
const changed = computed(() => eventDishChanges.value?.filter((dish) => dish.type === 'changed') ?? oldResourceDishChanges.value.changed)

const infoChanges = computed(() => {
  const information = eventChanges.value?.information
  if (!information || typeof information !== 'object') return []
  return Object.entries(information).map(([key, value]) => makeDetail(key, value))
})

const hasChanges = computed(() => {
  return Boolean(
    eventDishChanges.value?.length ||
    infoChanges.value.length ||
    resourceChanges.value?.has_changes ||
    added.value.length ||
    removed.value.length ||
    changed.value.length
  )
})

const orderLabel = computed(() => {
  const code = order.value?.short_code || props.orderEditLog?.order_id
  return code
    ? $t('wasabi.orderEditPopup.orderChangedWithCode', { code })
    : $t('wasabi.orderEditPopup.orderChangedGeneric')
})

const updateCustomScrollbar = () => {
  const el = scrollContent.value
  if (!el) return

  const scrollableHeight = el.scrollHeight - el.clientHeight
  customScrollbar.visible = scrollableHeight > 1

  if (!customScrollbar.visible) {
    customScrollbar.thumbHeight = 0
    customScrollbar.thumbTop = 0
    return
  }

  const thumbHeight = Math.max(44, (el.clientHeight / el.scrollHeight) * el.clientHeight)
  const maxThumbTop = el.clientHeight - thumbHeight

  customScrollbar.thumbHeight = thumbHeight
  customScrollbar.thumbTop = scrollableHeight ? (el.scrollTop / scrollableHeight) * maxThumbTop : 0
}

const viewFinalOrder = async () => {
  const orderId = order.value?.id || props.orderEditLog?.order_id

  if (order.value) {
    dishesStore.showingSingleOrder = order.value
  }

  emit('close')

  if (orderId) {
    await router.push({
      path: `/orders/${orderId}`,
      query: route.query,
    })
  }
}

const lockPageScroll = () => {
  lockedScrollY = window.scrollY
  previousBodyOverflow = document.body.style.overflow
  previousBodyPosition = document.body.style.position
  previousBodyTop = document.body.style.top
  previousBodyWidth = document.body.style.width

  document.body.style.overflow = 'hidden'
  document.body.style.position = 'fixed'
  document.body.style.top = `-${lockedScrollY}px`
  document.body.style.width = '100%'
}

const unlockPageScroll = () => {
  document.body.style.overflow = previousBodyOverflow
  document.body.style.position = previousBodyPosition
  document.body.style.top = previousBodyTop
  document.body.style.width = previousBodyWidth
  window.scrollTo(0, lockedScrollY)
}

watch(
  () => [infoChanges.value.length, added.value.length, removed.value.length, changed.value.length],
  async () => {
    await nextTick()
    updateCustomScrollbar()
  },
  { immediate: true }
)

onMounted(async () => {
  lockPageScroll()
  await nextTick()
  updateCustomScrollbar()
  window.addEventListener('resize', updateCustomScrollbar)

  if (scrollContent.value && 'ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(updateCustomScrollbar)
    resizeObserver.observe(scrollContent.value)
  }
})

onBeforeUnmount(() => {
  unlockPageScroll()
  window.removeEventListener('resize', updateCustomScrollbar)
  resizeObserver?.disconnect()
})
</script>

<style lang="scss" scoped>
.shadow {
  position: absolute;
  left: -88px;
  top: -79px;
  opacity: 30%;
  width: 360px;
  height: 360px;
  border-radius: 100%;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(117, 177, 59, 0.87) 0%,
    rgba(117, 177, 59, 0) 100%
  );
}

.custom-scroll-wrap {
  position: relative;
  min-height: 0;
}

.custom-scroll-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: min(44vh, 420px);
  overflow-y: auto;
  padding-right: 12px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.custom-scroll-content::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.custom-scrollbar {
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  border-radius: 999px;
  background: rgba(219, 225, 240, 0.08);
  pointer-events: none;
}

.custom-scrollbar-thumb {
  width: 100%;
  border-radius: 999px;
  background: linear-gradient(180deg, #75b13b 0%, rgba(117, 177, 59, 0.48) 100%);
  box-shadow: 0 0 12px rgba(117, 177, 59, 0.35);
}

.close-button {
  box-shadow:
    inset 0 0 0 1px rgba(219, 225, 240, 0.08),
    0 0 18px rgba(117, 177, 59, 0.12);
}

.change-block {
  border: 1px solid #75b13b57;
  border-radius: 14px;
  background: rgba(52, 68, 37, 0.8);
  padding: 12px;
}

.change-title {
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 800;
  color: #75b13b;
  text-transform: uppercase;
}

.change-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  font-size: 14px;
  font-weight: 700;
}

.dish-change {
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-bottom: 1px solid #75b13b30;
  padding: 8px 0;
}

.dish-change:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.change-detail {
  font-size: 12px;
  font-weight: 600;
  color: #dbe1f0;
  word-break: break-word;
}

.change-value {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.change-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #75b13b;
  font-size: 16px;
  font-weight: 700;
  line-height: 16px;
  width: 16px;
  height: 16px;
  margin: 0 1px;
  vertical-align: middle;
  transform: translateY(-1px);
}

.change-detail-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid #75b13b30;
  padding: 7px 0;
  font-size: 12px;
  font-weight: 600;
  color: #dbe1f0;
}

.change-detail-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
</style>
